// src/lib/vues-donnees.js — D'OÙ VIENNENT LES CHIFFRES D'UNE VUE.
//
// ══ POURQUOI CÔTÉ NAVIGATEUR, ET NON CÔTÉ SERVEUR ═══════════════════════════
//
// Le serveur CHOISIT la vue (son nom, ses arguments, tous deux filtrés par la
// liste fermée du registre). Il ne rassemble PAS ses chiffres. Trois raisons,
// dans l'ordre où elles pèsent :
//
//   1. Les chiffres ne passent jamais devant le modèle. Le prompt ne contient
//      que le relevé de faits — court, groupé, sans PII. Faire transiter une
//      série de revenus par la conversation reviendrait à l'exposer pour rien :
//      le modèle n'a pas besoin de les VOIR pour décider de les MONTRER.
//   2. Le chemin argent reste intouché. `aggregateStats` et
//      `projectDataForFacturation` sont des lectures d'argent, déjà lues par
//      les écrans existants. On ne leur ajoute pas un second appelant serveur
//      avec ses propres bornes — ce serait deux vérités sur la même somme.
//   3. La réponse arrive d'abord. Le texte s'affiche dès la fin de l'appel au
//      modèle ; le dessin le rejoint quand ses chiffres arrivent. Rassembler
//      côté serveur ferait attendre la phrase derrière les chiffres.
//
// ══ CE QUI EST BRANCHÉ, ET CE QUI NE L'EST PAS ══════════════════════════════
//
// TROIS vues sont branchées : `jours_a_venir` (lue ici, depuis la base), et
// `son_journal` / `sa_memoire` (demandées au serveur — voir plus bas). Les
// cinq autres sont DÉCLARÉES dans le registre et lisent l'argent
// (`aggregateStats`, `projectDataForFacturation`) — elles attendent leur
// propre lot, avec la revue que le chemin argent impose.
//
// ⚠️ ET ELLES LE DISENT. Une vue non branchée ne rend pas « aucune donnée »
// (qui se confondrait avec « rien à montrer ») : elle rend un MOTIF NOMMÉ, que
// l'écran affiche. Un dessin absent doit dire pourquoi il est absent, sinon
// Dimitri conclut que la donnée n'existe pas.

import { lireVue, VUES } from "./vues-registre.js";

// ⚠️ LA LISTE DES CHARGEURS EST LA SECONDE MOITIÉ D'UNE VÉRITÉ.
// Le registre porte `branchee: true/false` ; ici vivent les chargeurs. Les
// deux DOIVENT s'accorder, et un test le vérifie dans les deux sens
// (vues-donnees.test.js). Sans ce garde :
//   · `branchee: true` sans chargeur ⇒ le modèle promet une vue, l'écran rend
//     un vide poli — c'est le défaut mesuré en prod le 2026-09-08 ;
//   · un chargeur sans `branchee: true` ⇒ du travail fait, jamais offert.
// Deux listes tenues à la main divergent au premier ajout.

const JOURS_PAR_DEFAUT = 7;
const JOURS_MAX = 14; // le plafond du dessin (Jours.jsx) — au-delà il refuse

/** AAAA-MM-JJ, décalé de n jours. Pur, sans fuseau : ces dates sont des dates. */
export function decalerJour(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function dateValide(s) {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/**
 * Les jours qui viennent — arrivées, départs, ménages, sur une grille.
 *
 * ⚠️ ANTI-PII STRICT : on ne demande QUE `logement_id` et des DATES. Ni nom,
 * ni e-mail, ni téléphone, ni heure. Le dessin n'en affiche pas, et une
 * colonne qu'on ne demande pas ne peut pas fuir.
 *
 * ⚠️ CHAQUE SOURCE EST INDÉPENDANTE. Si les ménages ne répondent pas, on rend
 * quand même les arrivées et les départs — ET on le dit (`partiel`). Une
 * grille silencieusement amputée ressemblerait à une semaine calme.
 */
export async function chargerJoursAVenir(supabase, debut, nbJours) {
  const d0 = dateValide(debut) ? debut : new Date().toISOString().slice(0, 10);
  // ⚠️ Un nombre de jours absurde (0, négatif, NaN) retombe sur le DÉFAUT, pas
  // sur 1. `Math.max(-3, 1)` aurait rendu une grille d'UN jour — plausible à
  // l'écran, et fausse : Dimitri aurait cru sa semaine vide.
  const brut = Number(nbJours);
  const n =
    Number.isFinite(brut) && brut > 0
      ? Math.min(Math.floor(brut), JOURS_MAX)
      : JOURS_PAR_DEFAUT;
  const fin = decalerJour(d0, n - 1);
  const manquantes = [];

  const [resas, runs] = await Promise.all([
    supabase
      .from("reservations")
      .select("logement_id, start_date, end_date, status")
      // Une résa concerne la fenêtre si elle y ARRIVE ou si elle en PART.
      // On ne cherche pas les séjours qui la traversent : la grille montre des
      // mouvements, pas des occupations.
      .or(
        `and(start_date.gte.${d0},start_date.lte.${fin}),and(end_date.gte.${d0},end_date.lte.${fin})`,
      )
      .then((r) => r)
      .catch((e) => ({ error: e })),
    supabase
      .from("menage_runs")
      .select("logement_id, scheduled_date, status")
      .gte("scheduled_date", d0)
      .lte("scheduled_date", fin)
      .then((r) => r)
      .catch((e) => ({ error: e })),
  ]);

  if (resas.error) {
    console.error("[vues] réservations", resas.error?.message || resas.error);
    manquantes.push("arrivées et départs");
  }
  if (runs.error) {
    console.error("[vues] ménages", runs.error?.message || runs.error);
    manquantes.push("ménages");
  }
  // Les deux sources muettes : ce n'est pas une semaine vide, c'est une panne.
  if (manquantes.length === 2) {
    return { ok: false, motif: "je n'ai pas pu lire les jours qui viennent" };
  }

  const grille = new Map();
  for (let i = 0; i < n; i += 1) {
    const date = decalerJour(d0, i);
    grille.set(date, { date, arrivees: [], departs: [], menages: [] });
  }
  const pousser = (date, cle, id) => {
    const j = grille.get(date);
    if (j && typeof id === "string" && id) j[cle].push(id);
  };

  for (const r of resas.data || []) {
    // Une résa annulée n'est ni une arrivée ni un départ : l'afficher ferait
    // préparer un logement pour personne.
    if (r.status && String(r.status).toLowerCase().includes("cancel")) continue;
    pousser(r.start_date, "arrivees", r.logement_id);
    pousser(r.end_date, "departs", r.logement_id);
  }
  for (const m of runs.data || []) {
    // Un ménage annulé n'est pas un ménage à faire.
    if (m.status === "skipped" || m.status === "cancelled") continue;
    pousser(m.scheduled_date, "menages", m.logement_id);
  }

  return {
    ok: true,
    donnees: { jours: [...grille.values()] },
    partiel: manquantes.length ? manquantes.join(" et ") : null,
  };
}

// ── CE QUI EST À LUI — son journal, sa mémoire ──────────────────────────────
//
// Ces deux vues ne lisent PAS la base depuis le navigateur : leurs lignes sont
// agrégées côté serveur (lib/operateur-sien.js — cinq tables, fail-soft par
// source, noms de logements traduits, filet RGPD). Le navigateur les DEMANDE,
// avec le jeton de Dimitri (requireAdmin au routeur), et rend ce qu'il reçoit.
//
// ⚠️ Un appel qui échoue rend un MOTIF, jamais une liste vide : « je n'ai pas
// pu relire mon journal » et « je n'ai rien fait » ne sont pas la même phrase.

/** L'appel d'une action de l'opérateur, avec la session courante. */
export async function appelerOperateur(supabase, body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Session expirée");
  const res = await fetch("/api/beds24-mapping", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json) {
    const e = new Error(
      (json && (json.message || json.error)) || `Erreur ${res.status}`,
    );
    e.code = json && json.error;
    throw e;
  }
  return json;
}

/** Son journal — ce qu'il a fait depuis `depuis` (par défaut hier soir). */
export async function chargerSonJournal(supabase, depuis) {
  const json = await appelerOperateur(supabase, {
    action: "operateur_sien_journal",
    depuis: typeof depuis === "string" ? depuis.slice(0, 40) : null,
  });
  if (json.ok !== true || !Array.isArray(json.lignes)) {
    return { ok: false, motif: "je n'ai pas pu relire mon journal" };
  }
  return {
    ok: true,
    donnees: {
      lignes: json.lignes,
      vide: typeof json.vide === "string" ? json.vide : null,
      depuis_libelle: json.depuis_libelle || null,
    },
    partiel: null,
  };
}

/** Sa mémoire — ses consignes, avec la vie de chacune. */
export async function chargerSaMemoire(supabase) {
  const json = await appelerOperateur(supabase, {
    action: "operateur_sien_memoire",
  });
  if (json.ok !== true || !Array.isArray(json.consignes)) {
    return {
      ok: false,
      motif:
        (json && typeof json.motif === "string" && json.motif) ||
        "je n'ai pas pu relire ce que j'ai retenu",
    };
  }
  return {
    ok: true,
    donnees: {
      consignes: json.consignes,
      compte_existe: json.compte_existe === true,
      compte_motif:
        typeof json.compte_motif === "string" ? json.compte_motif : null,
    },
    partiel: null,
  };
}

/**
 * La croix « lui faire oublier » — le levier EXISTANT (`operateur_memoire_decide`,
 * geste `oublier`, lib/operateur-handlers.js), pas un second chemin d'oubli.
 * @returns {Promise<{ok:true}|{ok:false, motif:string}>}
 */
export async function oublierConsigne(supabase, id) {
  if (typeof id !== "string" || !id)
    return { ok: false, motif: "consigne inconnue" };
  try {
    const json = await appelerOperateur(supabase, {
      action: "operateur_memoire_decide",
      geste: "oublier",
      id,
    });
    return json.ok === true
      ? { ok: true }
      : { ok: false, motif: "l'oubli a été refusé" };
  } catch (e) {
    console.error("[vues] oubli", e?.message || e);
    return {
      ok: false,
      motif:
        e?.code === "deja_decidee"
          ? "cette consigne était déjà oubliée"
          : "je n'ai pas pu l'oublier — réessayez",
    };
  }
}

// ── LA TABLE DES CHARGEURS ─────────────────────────────────────────────────
// Une entrée par vue réellement branchée. Le nom de la clé est celui du
// registre — c'est ce qui rend le garde de cohérence possible.
const CHARGEURS = {
  jours_a_venir: ({ supabase, args }) =>
    // ⚠️ `debut` arrive parfois SYMBOLIQUE (« aujourd_hui ») : le modèle écrit
    // ce qu'il comprend, pas une date ISO. Vu en prod le 2026-09-08. Toute
    // valeur qui n'est pas une date valide retombe sur aujourd'hui — c'est
    // volontaire et testé, pas un heureux hasard.
    chargerJoursAVenir(supabase, args.debut, JOURS_PAR_DEFAUT),
  son_journal: ({ supabase, args }) => chargerSonJournal(supabase, args.depuis),
  sa_memoire: ({ supabase }) => chargerSaMemoire(supabase),
};

/**
 * Le résolveur : une vue validée → ses chiffres, ou un motif nommé.
 *
 * ⚠️ LISTE FERMÉE, comme partout ailleurs. Un nom inconnu ne tombe sur aucun
 * chargeur « par défaut » : il rend un refus. C'est la même défense que pour
 * les leviers — ce qui n'est pas explicitement branché n'existe pas.
 *
 * @returns {Promise<{ok:true, donnees:object, partiel:string|null}
 *                  |{ok:false, motif:string}>}
 */
export async function chargerDonneesVue(vue, { supabase } = {}) {
  if (!vue || typeof vue !== "object" || typeof vue.nom !== "string") {
    return { ok: false, motif: "aucune vue à préparer" };
  }
  const desc = lireVue(vue.nom);
  if (!desc) return { ok: false, motif: "cette vue n'existe pas" };
  if (!supabase) return { ok: false, motif: "pas d'accès aux données ici" };

  const args = vue.args || {};
  try {
    const chargeur = CHARGEURS[vue.nom];
    if (chargeur) return await chargeur({ supabase, args });
  } catch (e) {
    console.error("[vues] chargement", e?.message || e);
    return { ok: false, motif: "la lecture des chiffres a échoué" };
  }

  // Déclarée, pas branchée. On NOMME la raison : « aucune donnée » laisserait
  // croire qu'il n'y a rien à voir, alors que c'est nous qui ne savons pas
  // encore aller le chercher.
  return {
    ok: false,
    motif: `« ${desc.titre} » n'est pas encore branchée sur ses chiffres`,
  };
}

/** Exposé pour le garde de cohérence registre ↔ chargeurs (test). */
export const _CHARGEURS = CHARGEURS;
export const _VUES_DU_REGISTRE = VUES;

export const _JOURS_PAR_DEFAUT = JOURS_PAR_DEFAUT;
export const _JOURS_MAX = JOURS_MAX;
